/** @jsxImportSource @emotion/react */
'use memo'

import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import type { OrgRegistrationDivisionOption } from '@sportninja/common/features/organization/registration/types'
import {
  getFilledPercent,
  getSpotsLabel,
  getSpotsTone,
  type SpotsTone,
} from '@sportninja/common/features/organization/registration/utils/availability'
import { paragraphSmall, weightSemiBold } from 'src/components/css'
import { SPOTS_COLORS } from '../../constants/tones'

interface Props {
  /** The player or team option; null when the division doesn't offer it. */
  option: OrgRegistrationDivisionOption | null
  /**
   * Set for a registration that isn't open ("Closed", "Opens Jan 15"): live
   * spots mean nothing then, so the cell shows this instead.
   */
  inactiveLabel?: string
}

/**
 * Spots left for one kind of registration (player or team) in a division, with
 * a capacity meter while registration is open; a dash when the division
 * doesn't take that kind.
 */
const SpotsCell = ({ option, inactiveLabel }: Props) => {
  'use memo'

  if (!option) {
    return (
      <Wrapper>
        <Label tone='full'>—</Label>
      </Wrapper>
    )
  }

  if (inactiveLabel) {
    return (
      <Wrapper>
        <Label tone='full'>{inactiveLabel}</Label>
      </Wrapper>
    )
  }

  const tone = getSpotsTone(option.capacity, option.spotsLeft)
  const filled = getFilledPercent(option.capacity, option.spotsLeft)

  return (
    <Wrapper>
      {filled !== null && (
        <Meter aria-hidden='true'>
          <MeterFill tone={tone} percent={filled} />
        </Meter>
      )}
      <Label tone={tone}>{getSpotsLabel(option)}</Label>
    </Wrapper>
  )
}

export default SpotsCell

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
  white-space: nowrap;
`

const Meter = styled.div`
  width: 56px;
  height: 6px;
  flex: 0 0 56px;
  border-radius: 999px;
  overflow: hidden;
  background: ${colors.SECONDARY_200};
`

const MeterFill = styled.div<{ tone: SpotsTone; percent: number }>`
  width: ${({ percent }) => percent}%;
  height: 100%;
  border-radius: 999px;
  background: ${({ tone }) => SPOTS_COLORS[tone]};
`

const Label = styled.span<{ tone: SpotsTone }>`
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${({ tone }) => SPOTS_COLORS[tone]};
`
